import React from "react";
import { STATUS_COLORS, STATUS_DOT } from "@/lib/format";

export const StatusBadge = ({ status, testId }) => (
  <span
    data-testid={testId || `status-${String(status).toLowerCase().replace(/\s+/g, "-")}`}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${STATUS_COLORS[status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}
  >
    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status] || "bg-zinc-400"}`} />
    {status}
  </span>
);

export const Btn = React.forwardRef(({ as: As = "button", variant = "primary", size = "md", className = "", children, ...props }, ref) => {
  const base = "inline-flex items-center justify-center gap-2 font-medium rounded-full transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
  const sizes = {
    sm: "px-3.5 py-1.5 text-xs",
    md: "px-5 py-2 text-sm",
    lg: "px-6 py-2.5 text-sm",
  };
  const variants = {
    primary: "bg-zinc-950 text-white hover:bg-zinc-800 shadow-sm",
    secondary: "bg-white border border-zinc-200 text-zinc-950 hover:bg-zinc-50",
    destructive: "bg-red-500 text-white hover:bg-red-600 shadow-sm",
    ghost: "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
  };
  return (
    <As ref={ref} className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} {...props}>
      {children}
    </As>
  );
});

export const IconBtn = ({ children, className = "", variant = "ghost", ...props }) => {
  const variants = {
    ghost: "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900",
    destructive: "text-white bg-red-500 hover:bg-red-600",
    secondary: "text-zinc-700 bg-white border border-zinc-200 hover:bg-zinc-50",
  };
  return (
    <button
      className={`p-2 rounded-lg transition-colors ${variants[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

export const Input = React.forwardRef(({ className = "", ...props }, ref) => (
  <input
    ref={ref}
    className={`w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-950 placeholder:text-zinc-400 focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950 transition-all ${className}`}
    {...props}
  />
));

export const Textarea = React.forwardRef(({ className = "", ...props }, ref) => (
  <textarea
    ref={ref}
    className={`w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-950 placeholder:text-zinc-400 focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950 transition-all resize-y ${className}`}
    {...props}
  />
));

export const Select = React.forwardRef(({ className = "", children, ...props }, ref) => (
  <select
    ref={ref}
    className={`w-full px-3.5 py-2.5 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-950 focus:ring-2 focus:ring-zinc-950 focus:border-zinc-950 transition-all ${className}`}
    {...props}
  >
    {children}
  </select>
));

export const Label = ({ children, required, className = "", ...props }) => (
  <label className={`block text-sm font-medium text-zinc-900 mb-1.5 ${className}`} {...props}>
    {children}
    {required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

export const Card = ({ children, className = "", ...props }) => (
  <div className={`bg-white border border-zinc-200 rounded-xl ${className}`} {...props}>
    {children}
  </div>
);

export const PageHeader = ({ title, subtitle, actions, testId }) => (
  <div data-testid={testId} className="flex items-start justify-between gap-4 mb-8">
    <div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-zinc-950 tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-zinc-500 mt-1.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

export const Empty = ({ title, description, action }) => (
  <div className="text-center py-16 animate-fade-in">
    <p className="font-display text-xl text-zinc-900 mb-1.5">{title}</p>
    {description && <p className="text-sm text-zinc-500 mb-4">{description}</p>}
    {action}
  </div>
);

export const Spinner = ({ className = "w-5 h-5" }) => (
  <div className={`${className} border-2 border-zinc-300 border-t-zinc-950 rounded-full animate-spin`} />
);
